import { ApiService, Doctor, Review, WaitlistEmail } from './types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL!;

export class ApiServiceImpl implements ApiService {
  private async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    return response.json();
  }

  // Doctors
  async getDoctors(): Promise<Doctor[]> {
    return this.fetch<Doctor[]>('/doctors');
  }

  async getDoctor(id: string): Promise<Doctor | null> {
    return this.fetch<Doctor | null>(`/doctors/${id}`);
  }

  async addDoctor(doctor: Omit<Doctor, 'id' | 'created_at' | 'updated_at'>): Promise<Doctor> {
    return this.fetch<Doctor>('/doctors', {
      method: 'POST',
      body: JSON.stringify(doctor),
    });
  }

  async updateDoctor(id: string, doctor: Partial<Doctor>): Promise<Doctor> {
    return this.fetch<Doctor>(`/doctors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(doctor),
    });
  }

  async deleteDoctor(id: string): Promise<void> {
    await this.fetch(`/doctors/${id}`, {
      method: 'DELETE',
    });
  }

  // Reviews
  async getReviews(): Promise<Review[]> {
    return this.fetch<Review[]>('/reviews');
  }

  async getDoctorReviews(doctorId: string): Promise<Review[]> {
    return this.fetch<Review[]>(`/doctors/${doctorId}/reviews`);
  }

  async addReview(review: Omit<Review, 'id' | 'created_at' | 'updated_at'>): Promise<Review> {
    return this.fetch<Review>('/reviews', {
      method: 'POST',
      body: JSON.stringify(review),
    });
  }

  async updateReview(id: string, review: Partial<Review>): Promise<Review> {
    return this.fetch<Review>(`/reviews/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(review),
    });
  }

  async deleteReview(id: string): Promise<void> {
    await this.fetch(`/reviews/${id}`, {
      method: 'DELETE',
    });
  }

  // Waitlist
  async addWaitlistEmail(email: string): Promise<WaitlistEmail> {
    return this.fetch<WaitlistEmail>('/waitlist', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }
}

// Export a singleton instance
export const apiService = new ApiServiceImpl(); 